import { useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { integerPattern } from "../../Utils/urlState/integerPattern";
import { getIsRealTime } from "../../Utils/urlState/getIsRealTime";
import {
    getDateShownFromSearchParams,
    getSearchString,
} from "../../Utils/urlState/searchParams";
import { DateShown } from "../../Utils/urlState/DateShown";
import { useDateSelected } from "../../Utils/urlState/useDateSelected";
import { CCGT_ROUTE } from "../../Utils/AppRoutes";

/**
 * useUrlStateCCGT manages the state used by CCGTPage that is stored in the page's url, either in the path (plantName) or in the search parameters (dateSelected).
 *
 * It returns both the state's current value, retrieved from the page's url and all needed setters, which work by computing the url corresponding to the new state and navigating to the new url.
 *
 * @returns
 */
export const useUrlStateCCGT = () => {
    const { plantName: param } = useParams();
    

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // state saved in the the url
    const plantName = useMemo(() => getPlantName(param), [param]);
    
    
    const dateShown: DateShown = useMemo(
        () => getDateShownFromSearchParams(searchParams),
        [searchParams]
    );

    const isRealTime = useMemo(() => getIsRealTime(dateShown), [dateShown]);

    const setUrlState = useCallback(
        (state: { plantName: string | undefined; dateShown: DateShown }) => {
            const url = getUrl(state);
            navigate(url);
        },
        [navigate]
    );

    const state = useMemo(() => {
        return { plantName };
    }, [plantName]);

    const { dateSelected, setDateSelected } = useDateSelected(
        dateShown,
        setUrlState,
        state
    );

    return {
        plantName,
        dateSelected,
        setDateSelected,
        dateShown,
        isRealTime,
    };
};

/**
 * getUrl takes the current state and computes the corresponding url
 * @param state
 * @returns
 */
const getUrl = ({
    plantName,
    dateShown,
}: {
    plantName: string | undefined;
    dateShown: DateShown;
}) => {
    const searchParams = new URLSearchParams();
    if (!getIsRealTime(dateShown)) {
        searchParams.set("from", dateShown[0].toISODate());
        searchParams.set("to", dateShown[1].toISODate());
    }

    const query = getSearchString(searchParams);
    const param = typeof plantName === "string" ? "/" + plantName : "";

    return CCGT_ROUTE + param + query;
};

const getPlantName = (param: string | undefined): string | undefined => {
    if (typeof param === "string") {
        return param;
    } else {
        return undefined;
    }
};
